import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { login, verifyOtp } from '../../services/auth';
import { fetchAdminUserByMobile } from '../../services/admin';
import { UserRole, User, Permission } from '../../types/auth';

// Define types for API responses
interface OtpVerificationResponse {
  token: string;
  role: UserRole;
  name?: string;
  email?: string;
  suspended?: boolean;
  activated?: boolean;
  lastLogin?: string;
  dashPermissions?: Permission[];
  groups?: string[]; // Add groups array for cleverpeuser role
  lender? : string[];
}

// Define the type for our auth state
interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  otpHash: string | null;
  mobileNo: string | null;
}

// Function to load the stored user from localStorage
const loadStoredUser = (): User | null => {
  const storedUser = localStorage.getItem('user');
  console.log('[DEBUG] Stored user data:', storedUser);
  
  if (storedUser) {
    try {
      // Parse the stored user data
      const parsedUser = JSON.parse(storedUser);
      console.log('[DEBUG] Parsed user data:', parsedUser);
      
      // Verify that the parsed data has required fields
      if (!parsedUser.role || !parsedUser.mobileNo) {
        console.error('[DEBUG] Stored user data is missing required fields: role or mobileNo');
        localStorage.removeItem('user'); // Clear invalid data
        return null;
      }
      
      // Create a valid user object
      const user: User = {
        name: parsedUser.name || '',
        email: parsedUser.email || '',
        role: parsedUser.role,
        mobileNo: parsedUser.mobileNo,
        suspended: parsedUser.suspended === true,
        activated: parsedUser.activated === true,
        lastLogin: parsedUser.lastLogin || '',
        dashPermissions: parsedUser.dashPermissions || [],
        groups: parsedUser.groups || [] ,// Add groups to the user object
        lender : parsedUser.lender || [] // Include lender field if it exists
      };
      
      return user;
    } catch (error) {
      console.error('[DEBUG] Failed to parse stored user data:', error);
      localStorage.removeItem('user'); // Clear invalid data
      return null;
    }
  }
  return null;
};

// Initial state
const initialState: AuthState = {
  user: loadStoredUser(),
  token: localStorage.getItem('token') || null,
  isAuthenticated: !!(localStorage.getItem('token') && loadStoredUser()),
  loading: false,
  error: null,
  otpHash: null,
  mobileNo: null
};

// Async thunk for sending OTP
export const sendOTP = createAsyncThunk(
  'auth/sendOTP',
  async (mobileNo: string, { rejectWithValue }) => {
    try {
      console.log('[redux] Sending OTP to:', mobileNo);
      
      const response = await login(mobileNo);
      console.log('[redux] Received response from login:', response);
      
      if (!response.otpHash) {
        console.error('[redux] Missing otpHash in response');
        return rejectWithValue('Failed to get OTP hash from server');
      }
      
      return { otpHash: response.otpHash, mobileNo };
    } catch (error) {
      console.error('[redux] Send OTP error:', error);
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('An unknown error occurred while sending OTP');
    }
  }
);

// Async thunk for verifying OTP
export const verifyOTPThunk = createAsyncThunk(
  'auth/verifyOTP',
  async ({ mobileNo, otp, otpHash }: { mobileNo: string; otp: string; otpHash: string }, { rejectWithValue, getState }) => {
    try {
      console.log('[redux] Verifying OTP with params:', { 
        mobileNo: mobileNo ? `${mobileNo.substring(0, 3)}...` : 'missing', 
        otpHash: otpHash ? `${otpHash.substring(0, 5)}...` : 'missing',
        otp: otp ? `${otp.length} digits` : 'missing'
      });
      
      // Try to get values from sessionStorage first if not provided
      const sessionMobileNo = sessionStorage.getItem('mobileNo');
      const sessionOtpHash = sessionStorage.getItem('otpHash');
      
      console.log('[redux] Values from sessionStorage:', {
        mobileNo: sessionMobileNo ? `${sessionMobileNo.substring(0, 3)}...` : 'missing',
        otpHash: sessionOtpHash ? `${sessionOtpHash.substring(0, 5)}...` : 'missing'
      });
      
      // Use provided values or fallback to sessionStorage
      const finalMobileNo = mobileNo || sessionMobileNo || '';
      const finalOtpHash = otpHash || sessionOtpHash || '';
      
      if (!finalMobileNo || !finalOtpHash) {
        console.error('[redux] Missing required data for OTP verification:', {
          hasMobileNo: !!finalMobileNo,
          hasOtpHash: !!finalOtpHash
        });
        return rejectWithValue('Session expired. Please enter your mobile number again.');
      }
      
      // Log what we're sending to the API
      console.log('[redux] Sending to verifyOtp API:', {
        mobileNo: finalMobileNo ? `${finalMobileNo.substring(0, 3)}...` : 'missing',
        otpHash: finalOtpHash ? `${finalOtpHash.substring(0, 5)}...` : 'missing',
        otp
      });
      
      // Verify OTP to get token and user data
      const apiResponse = await verifyOtp(finalMobileNo, otp, finalOtpHash);
      
      // Validate response structure
      if (!apiResponse || typeof apiResponse !== 'object') {
        return rejectWithValue('Invalid response from server');
      }
      
      // Access token directly
      const { token, user } = apiResponse as any;
      
      if (!token) {
        return rejectWithValue('Token missing in response');
      }
      
      console.log('[redux] Storing token in localStorage');
      localStorage.setItem('token', token);
      localStorage.setItem('session_heartbeat', Date.now().toString());
      
      // Extract user data from response
      const userData: User = {
        name: user.name || '',
        email: user.email || '',
        role: user.role,
        mobileNo: finalMobileNo,
        suspended: user.suspended === true,
        activated: user.activated === true,
        lastLogin: user.lastLogin || '',
        dashPermissions: user.dashPermissions || [],
        groups: user.groups || [], // Include groups array from the response
        lender : user.lender || [] // Include lender field if it exists
      };
      
      // Store the complete user object
      localStorage.setItem('user', JSON.stringify(userData));
      
      console.log('[redux] Complete user data stored:', userData);
      
      // Clear OTP related info after successful verification
      sessionStorage.removeItem('otpHash');
      sessionStorage.removeItem('mobileNo');
      
      // Return user object and token
      return { 
        token: token,
        user: userData
      };
    } catch (error) {
      console.error('[redux] OTP verification failed:', error);
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('An unknown error occurred during verification');
    }
  }
);

// Async thunk to validate token and restore user session
export const restoreSession = createAsyncThunk(
  'auth/restoreSession',
  async (_, { rejectWithValue }) => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    
    if (!token || !storedUser) {
      return rejectWithValue('No stored session found');
    }
    
    try {
      // Parse stored user data
      const parsedUser = JSON.parse(storedUser);
      
      // Validate required fields - require mobileNo and role
      if (!parsedUser.role || !parsedUser.mobileNo) {
        console.error('[redux] Missing required fields: mobileNo or role');
        throw new Error('Invalid user data in localStorage');
      }
      
      // Use mobileNo as ID if not present
      if (!parsedUser.id && parsedUser.mobileNo) {
        parsedUser.id = parsedUser.mobileNo;
      }
      
      // Ensure we have a complete user object
      const user: User = {
        name: parsedUser.name || '',
        email: parsedUser.email || '',
        role: parsedUser.role,
        mobileNo: parsedUser.mobileNo,
        suspended: parsedUser.suspended === true,
        activated: parsedUser.activated === true,
        lastLogin: parsedUser.lastLogin || '',
        dashPermissions: parsedUser.dashPermissions || [],
        groups: parsedUser.groups || [] ,// Add groups to the user object
        lender : parsedUser.lender || [] // Include lender field if it exists
      };
      
      console.log('[redux] Restoring validated user session:', {
        user: {
          mobileNo: user.mobileNo,
          name: user.name,
          email: user.email,
          role: user.role,
          dashPermissions: user.dashPermissions
        }
      });
      
      // In a real app, you would validate the token with the server here
      return { user, token };
    } catch (error) {
      console.error('[redux] Failed to restore session:', error);
      // Clean up invalid localStorage data
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      return rejectWithValue('Invalid stored session');
    }
  }
);

// Auth slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.isAuthenticated = true;
      // Store user in localStorage
      localStorage.setItem('user', JSON.stringify(action.payload));
    },
    clearAuth: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('session_heartbeat');  
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    expireOTP: (state) => {
      state.otpHash = null;
      // Clear OTP hash from sessionStorage as well
      sessionStorage.removeItem('otpHash');
    }
  },
  extraReducers: (builder) => {
    // Handle sendOTP
    builder.addCase(sendOTP.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(sendOTP.fulfilled, (state, action) => {
      state.loading = false;
      state.otpHash = action.payload.otpHash;
      state.mobileNo = action.payload.mobileNo;
      
      // Store in sessionStorage as backup in case state gets cleared
      sessionStorage.setItem('otpHash', action.payload.otpHash);
      sessionStorage.setItem('mobileNo', action.payload.mobileNo);
    });
    builder.addCase(sendOTP.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
    
    // Handle verifyOTP
    builder.addCase(verifyOTPThunk.pending, (state) => {
      state.loading = true;
      state.error = null;
      
      // Restore from sessionStorage if missing in state
      if (!state.otpHash) {
        state.otpHash = sessionStorage.getItem('otpHash');
      }
      if (!state.mobileNo) {
        state.mobileNo = sessionStorage.getItem('mobileNo');
      }
    });
    builder.addCase(verifyOTPThunk.fulfilled, (state, action) => {
      state.loading = false;
      
      // Ensure we have valid token and role data
      if (!action.payload.token || !action.payload.user || !action.payload.user.role) {
        console.error('[redux] Invalid user data in verifyOTPThunk response:', action.payload);
        state.error = 'Invalid user data received from server';
        return;
      }
      
      const { token, user } = action.payload;
      
      // Log the user object to debug
      console.log('[redux] User data from API before validation:', user);
      
      // Ensure we have a mobileNo - use the one from state if not in the response
      if (!user.mobileNo && state.mobileNo) {
        console.log('[redux] Using mobileNo from state:', state.mobileNo);
        user.mobileNo = state.mobileNo;
      }
      
      // Process the dashPermissions array - ensure it exists as at least an empty array
      if (!user.dashPermissions) {
        console.log('[redux] No dashPermissions in response, using empty array');
        user.dashPermissions = [];
      } else {
        console.log('[redux] Received dashPermissions:', user.dashPermissions);
      }
      
      // Process the groups array - ensure it exists as at least an empty array for cleverpeuser role
      if (!user.groups) {
        console.log('[redux] No groups in response, using empty array');
        user.groups = [];
      } else {
        console.log('[redux] Received groups:', user.groups);
      }
      
      // Create a complete user object with defaults for missing fields
      const validatedUser: User = {
        name: user.name || '',
        email: user.email || '',
        role: user.role as UserRole,
        mobileNo: user.mobileNo || (state.mobileNo || ''),
        suspended: user.suspended === true,
        activated: user.activated === true,
        lastLogin: user.lastLogin || '',
        dashPermissions: user.dashPermissions || [],
        groups: user.groups || [],
        lender: user.lender || [] // Include lender field if it exists
      };
      
      // Update state with validated data
      state.token = token;
      state.user = validatedUser;
      state.isAuthenticated = true;
      
      // Store properly validated data in localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(validatedUser));
      
      console.log('[redux] User data stored in localStorage:', validatedUser);
      
      // Clear OTP related info after successful verification
      state.otpHash = null;
      state.mobileNo = null;
      
      // Clean up sessionStorage
      sessionStorage.removeItem('otpHash');
      sessionStorage.removeItem('mobileNo');
    });
    builder.addCase(verifyOTPThunk.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
    
    // Handle restoreSession
    builder.addCase(restoreSession.fulfilled, (state, action) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.isAuthenticated = true;
      state.loading = false;
    });
    builder.addCase(restoreSession.rejected, (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.loading = false;
      // Clean up localStorage to prevent future issues
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    });
  }
});

// Export actions and reducer
export const { setUser, clearAuth, setError, expireOTP } = authSlice.actions;
export default authSlice.reducer; 